// src/services/baitCashApi.js

const API_BASE_URL = "https://backend.bait-coin.com/api/users";

// ==========================================================
// NORMALIZE PAKISTAN PHONE NUMBER
// ==========================================================

export const normalizePhone = (phone) => {
  if (!phone) return "";

  let cleaned = String(phone).replace(/\D/g, "");

  // +92XXXXXXXXXX / 92XXXXXXXXXX
  if (cleaned.startsWith("92")) {
    cleaned = cleaned.substring(2);
  }

  // Convert 3XXXXXXXXX -> 03XXXXXXXXX
  if (!cleaned.startsWith("03")) {
    cleaned = "03" + cleaned;
  }

  // Extra safety for 3XXXXXXXXX format
  if (cleaned.length === 10 && cleaned.startsWith("3")) {
    cleaned = "03" + cleaned;
  }

  return cleaned;
};

// ==========================================================
// RESOLVE RECIPIENT BY PHONE
// ==========================================================

export const resolveRecipient = async (phoneNumber) => {
  const normalized = normalizePhone(phoneNumber);

  console.log("📱 Resolving recipient (API):", normalized);

  const response = await fetch(`${API_BASE_URL}/resolve-phone`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({
      phoneNumber: normalized,
    }),
  });

  let data;

  try {
    data = await response.json();
  } catch (error) {
    console.error("❌ Invalid JSON response from resolve-phone:", error);

    throw new Error(
      `Server returned an invalid response (${response.status})`
    );
  }

  console.log("📱 Resolve response (API):", data);

  // ========================================================
  // HANDLE BACKEND ERROR
  // ========================================================

  if (!response.ok || !data.success) {
    throw new Error(data.error || "Unable to find recipient");
  }

  // ========================================================
  // NORMALIZE RECIPIENT
  // ========================================================

  const recipient = {
    id:
      data.recipient?.id ||
      data.recipient?._id ||
      data.recipientId ||
      data.userId ||
      data.user?._id ||
      data.user?.id ||
      data.id ||
      null,

    fullName:
      data.recipient?.fullName ||
      data.recipient?.name ||
      data.recipientName ||
      data.user?.fullName ||
      data.user?.name ||
      data.fullName ||
      data.name ||
      "Unknown",

    phoneNumber:
      data.recipient?.phoneNumber ||
      data.recipient?.phone ||
      data.phoneNumber ||
      data.recipientPhone ||
      data.phone ||
      normalized,

    walletAddress:
      data.recipient?.walletAddress ||
      data.recipient?.wallet ||
      data.walletAddress ||
      data.user?.walletAddress ||
      data.user?.wallet ||
      data.wallet ||
      null,

    phoneVerified:
      data.recipient?.phoneVerified ??
      data.phoneVerified ??
      data.user?.phoneVerified ??
      false,

    status:
      data.recipient?.status ||
      data.status ||
      data.user?.status ||
      "pending",
  };

  console.log("✅ Normalized recipient for Dashboard:", recipient);

  // ========================================================
  // VERIFY RECIPIENT EXISTS
  // ========================================================

  if (!recipient.phoneNumber) {
    console.error(
      "❌ Recipient phone number missing from backend response:",
      data
    );

    throw new Error("Recipient BAIT Cash account was not found.");
  }

  // ========================================================
  // NORMALIZE BAIT CASH ACCOUNT
  // ========================================================

  const baitCash = data.baitCash
    ? {
        id:
          data.baitCash.id ||
          data.baitCash._id ||
          data.baitCash.accountId ||
          data.baitCashId ||
          data.accountId ||
          null,

        balancePKR:
          data.baitCash.balancePKR ??
          data.baitCash.balance ??
          data.balancePKR ??
          0,

        status:
          data.baitCash.status ||
          data.baitCash.accountStatus ||
          data.baitCashStatus ||
          "pending",

        currency:
          data.baitCash.currency ||
          data.currency ||
          "PKR",
      }
    : {
        id: data.baitCashId || data.accountId || null,

        balancePKR: data.balancePKR ?? 0,

        status:
          data.baitCashStatus ||
          data.status ||
          "pending",

        currency: data.currency || "PKR",
      };

  console.log("💰 Normalized BAIT Cash account:", baitCash);

  // ========================================================
  // RETURN FORMAT EXPECTED BY Dashboard.js
  // ========================================================

  return {
    recipient,
    baitCash,
  };
};

// ==========================================================
// GET BAIT CASH BALANCE BY PHONE
// ==========================================================

export const getBaitCashBalance = async (phoneNumber) => {
  const normalized = normalizePhone(phoneNumber);

  console.log("💰 Fetching BAIT Cash balance for:", normalized);

  const response = await fetch(
    `${API_BASE_URL}/balance/phone/${encodeURIComponent(normalized)}`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    }
  );

  let data;

  try {
    data = await response.json();
  } catch (error) {
    console.error("❌ Invalid JSON response from balance API:", error);

    throw new Error(
      `Server returned an invalid response (${response.status})`
    );
  }

  console.log("💰 BAIT Cash balance response:", data);

  if (!response.ok || !data.success) {
    throw new Error(data.error || "Unable to fetch balance");
  }

  return data;
};

// ==========================================================
// TRANSFER BAIT CASH
// ==========================================================

export const transferBaitCash = async ({
  senderPhone,
  recipientPhone,
  amountPKR,
  idempotencyKey,
}) => {
  const normalizedSender = normalizePhone(senderPhone);
  const normalizedRecipient = normalizePhone(recipientPhone);

  console.log("💸 BAIT Cash transfer request:", {
    senderPhone: normalizedSender,
    recipientPhone: normalizedRecipient,
    amountPKR,
    idempotencyKey,
  });

  // ========================================================
  // BASIC VALIDATION
  // ========================================================

  if (!normalizedSender) {
    throw new Error("Sender phone number is required.");
  }

  if (!normalizedRecipient) {
    throw new Error("Recipient phone number is required.");
  }

  if (!amountPKR || Number(amountPKR) <= 0) {
    throw new Error("Transfer amount must be greater than zero.");
  }

  if (!idempotencyKey) {
    throw new Error("Transfer idempotency key is required.");
  }

  // ========================================================
  // PREVENT SENDING TO SAME NUMBER
  // ========================================================

  if (normalizedSender === normalizedRecipient) {
    throw new Error("You cannot transfer BAIT Cash to yourself.");
  }

  // ========================================================
  // SEND TRANSFER REQUEST
  // ========================================================

  const response = await fetch(`${API_BASE_URL}/transfer`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({
      senderPhone: normalizedSender,
      recipientPhone: normalizedRecipient,
      amountPKR: String(amountPKR),
      idempotencyKey,
    }),
  });

  let data;

  try {
    data = await response.json();
  } catch (error) {
    console.error("❌ Invalid JSON response from transfer API:", error);

    throw new Error(
      `Server returned an invalid response (${response.status})`
    );
  }

  console.log("💸 BAIT Cash transfer response:", data);

  // ========================================================
  // HANDLE TRANSFER ERROR
  // ========================================================

  if (!response.ok || !data.success) {
    throw new Error(
      data.error ||
        data.message ||
        "BAIT Cash transfer failed"
    );
  }

  // ========================================================
  // RETURN BACKEND RESPONSE
  // ========================================================

  return data;
};

// ==========================================================
// GET BAIT CASH TRANSACTIONS
// ==========================================================

export const getBaitCashTransactions = async (phoneNumber) => {
  const normalized = normalizePhone(phoneNumber);

  console.log(
    "📜 Fetching BAIT Cash transactions for:",
    normalized
  );

  const response = await fetch(
    `${API_BASE_URL}/transactions/${encodeURIComponent(normalized)}`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    }
  );

  let data;

  try {
    data = await response.json();
  } catch (error) {
    console.error(
      "❌ Invalid JSON response from transactions API:",
      error
    );

    throw new Error(
      `Server returned an invalid response (${response.status})`
    );
  }

  console.log(
    "📜 BAIT Cash transactions response:",
    data
  );

  if (!response.ok || !data.success) {
    throw new Error(
      data.error ||
        data.message ||
        "Unable to fetch transactions"
    );
  }

  return data;
};